import { createReducer, createSelector } from '@reduxjs/toolkit';
import {
  addCompanyHearingRolesToStore,
  getAllHearingsSuccess,
  getAllUsersSuccess,
  getHearingSuccess,
  getPaginatedCommentsForHearingSuccess,
  removeCompanyHearingRolesFromStore,
  uploadCsvInviteesSuccess,
  uploadExcelInviteesSuccess,
  uploadInvitationGroupInviteesSuccess,
  uploadPersonalInviteesSuccess,
} from '../actions/api';
import { COMPANYHEARINGROLE_INCLUDE } from '../../constants/api';
import {
  getIncludedData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import { hearingRoleByRoleSelector } from './hearingRoleSlice';
import { getEntityId, getRelId } from '../helpers/apiHelpers';

// -------- Overview --------

// CompanyHearingRole is included in the following endpoints
//  - GET /user
//  - GET /user/{id}
//  - GET /hearing
//  - GET /hearing/{id}
//  - GET /hearing/{hearingId}/comment

// -------- Reducer --------
const companyHearingRoleReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllUsersSuccess, (state, action) => {
        const data = getIncludedData(action);
        const companyHearingRole = data.filter(
          (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', companyHearingRole);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const companyHearingRoles = data.filter(
          (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', companyHearingRoles);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const companyHearingRoles = data.filter(
          (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', companyHearingRoles);
      })
      .addCase(getPaginatedCommentsForHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const companyHearingRoles = data.filter(
          (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', companyHearingRoles);
      })
      .addCase(addCompanyHearingRolesToStore, (state, action) => {
        return updateDtoList(state, 'all', action.payload);
      })
      .addCase(removeCompanyHearingRolesFromStore, (state, action) => {
        const idToRemove = action.payload;
        return removeItemFromList(state, 'all', idToRemove);
      })
      .addCase(uploadCsvInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const companyHearingRoles = data.filter(
          (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', companyHearingRoles);
      })
      .addCase(uploadExcelInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const companyHearingRoles = data.filter(
          (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', companyHearingRoles);
      })
      .addCase(uploadPersonalInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const companyHearingRoles = data.filter(
          (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', companyHearingRoles);
      })
      .addCase(uploadInvitationGroupInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const companyHearingRoles = data.filter(
          (include) => include.type === COMPANYHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', companyHearingRoles);
      });
  },
);

// -------- Selectors --------

const selectAllCompanyHearingRoles = (state) =>
  selectAllFromApiState(state, 'companyHearingRole');

const companyHearingRolesByIdsSelector = (companyHearingRoleIds) =>
  createSelector(selectAllCompanyHearingRoles, (companyHearingRoles) =>
    companyHearingRoles.filter((chr) =>
      companyHearingRoleIds.some((id) => getEntityId(chr) === id),
    ),
  );

const allCompanyHearingRolesWithRoleSelector = (role) =>
  createSelector(
    [selectAllCompanyHearingRoles, hearingRoleByRoleSelector(role)],
    (companyHearingRoles, hearingRole) =>
      companyHearingRoles.filter(
        (chr) => getRelId(chr, 'hearingRole') === getEntityId(hearingRole),
      ),
  );

const allCompanyHearingRolesWithRoleByHearingIdSelector = (role, hearingId) =>
  createSelector(
    allCompanyHearingRolesWithRoleSelector(role),
    (companyHearingRoles) =>
      companyHearingRoles.filter(
        (chr) => getRelId(chr, 'hearing') === hearingId,
      ),
  );

// -------- Exporting --------
export {
  companyHearingRolesByIdsSelector,
  allCompanyHearingRolesWithRoleByHearingIdSelector,
};
export default companyHearingRoleReducer;
